import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Ship, Package, CreditCard, GraduationCap, ArrowRight, Check } from "lucide-react";
import { showLaunchingSoon } from "@/lib/notify";

const AUDIENCES = [
    {
        id: "importers",
        label: "Importers",
        icon: Package,
        pain: "Paying overseas suppliers eats into margins because of opaque bank FX and slow wire settlements.",
        benefit: "Send INR-funded payments to 100+ countries in <24 hrs at interbank rates, with IDPMS auto-filed.",
        benefits: [
            "Lower landed cost on every shipment",
            "Pay suppliers in their local currency",
            "Automated IDPMS reporting",
        ],
    },
    {
        id: "exporters",
        label: "Exporters",
        icon: Ship,
        pain: "Foreign buyers pay slow and banks pocket 2–5% on inward remittances before EDPMS even closes.",
        benefit: "Collect global payouts in USD/GBP/CAD, convert at transparent rates, and auto-generate FIRA/FIRC.",
        benefits: [
            "Faster INR credit (<24 hrs)",
            "Higher realised value per invoice",
            "Auto FIRA/FIRC + EDPMS closure",
        ],
    },
    {
        id: "lrs",
        label: "LRS Users",
        icon: GraduationCap,
        pain: "Tuition, investments and family transfers under LRS get hit with 1–3% bank spreads and TCS confusion.",
        benefit: "Send up to $250K under LRS with live mid-market FX, TCS-aware flows and complete compliance trail.",
        benefits: [
            "Transparent LRS pricing",
            "TCS-aware quote engine",
            "Full Form A2 + 15CA/CB workflow",
        ],
    },
    {
        id: "cards",
        label: "Card Payment Users",
        icon: CreditCard,
        pain: "International card payments add 3.5%+ in markup, FX conversion and cross-border fees on every charge.",
        benefit: "Pay SaaS, ads and vendor invoices with transparent FX and consolidated INR reporting.",
        benefits: [
            "No surprise cross-border fees",
            "Single reconciled INR statement",
            "GST-ready invoice mapping",
        ],
    },
];

export default function AudienceTabs() {
    return (
        <section
            id="product"
            data-testid="audience-section"
            className="bg-white py-24 md:py-32 px-4 md:px-8"
        >
            <div className="mx-auto max-w-7xl">
                <div className="max-w-2xl">
                    <span className="text-xs tracking-[0.25em] uppercase font-bold text-emerald-700">
                        Who is TruoPay for
                    </span>
                    <h2 className="font-heading mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-zinc-950">
                        Built for the way you move money — pick your flow.
                    </h2>
                    <p className="mt-4 text-zinc-600">
                        Whatever side of the cross-border equation you sit on, TruoPay collapses
                        the cost, time and paperwork into a single workflow.
                    </p>
                </div>

                <Tabs defaultValue="importers" className="mt-10">
                    <TabsList
                        data-testid="audience-tabs-list"
                        className="h-auto w-full flex flex-wrap gap-2 bg-zinc-100 p-1.5 rounded-2xl"
                    >
                        {AUDIENCES.map((a) => (
                            <TabsTrigger
                                key={a.id}
                                value={a.id}
                                data-testid={`audience-tab-${a.id}`}
                                className="flex-1 min-w-[140px] h-12 rounded-xl text-sm font-bold data-[state=active]:bg-zinc-950 data-[state=active]:text-white data-[state=active]:shadow-md text-zinc-700"
                            >
                                <a.icon className="h-4 w-4 mr-2" />
                                {a.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {AUDIENCES.map((a) => (
                        <TabsContent key={a.id} value={a.id} className="mt-8">
                            <div className="grid lg:grid-cols-12 gap-6">
                                <div className="lg:col-span-7 rounded-3xl border border-zinc-200 bg-zinc-50 p-8 sm:p-10">
                                    <div className="inline-flex items-center gap-2 rounded-full bg-red-50 text-red-700 px-3 py-1 text-[11px] tracking-[0.2em] uppercase font-bold">
                                        The pain
                                    </div>
                                    <p className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-zinc-950 leading-snug">
                                        {a.pain}
                                    </p>
                                    <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-[11px] tracking-[0.2em] uppercase font-bold">
                                        With TruoPay
                                    </div>
                                    <p className="mt-3 text-base sm:text-lg text-zinc-700">
                                        {a.benefit}
                                    </p>
                                </div>
                                <div className="lg:col-span-5 rounded-3xl bg-gradient-to-br from-[#04110D] to-[#063a2c] text-white p-8 sm:p-10 flex flex-col">
                                    <div className="text-xs tracking-[0.25em] uppercase font-bold text-emerald-300">
                                        What you'll get
                                    </div>
                                    <ul className="mt-5 space-y-3 flex-1">
                                        {a.benefits.map((b) => (
                                            <li key={b} className="flex items-start gap-3 text-base">
                                                <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/40">
                                                    <Check className="h-3 w-3 text-emerald-300" />
                                                </span>
                                                <span className="text-zinc-200">{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        type="button"
                                        data-testid={`audience-explore-${a.id}-btn`}
                                        onClick={() => showLaunchingSoon(`Explore for ${a.label}`)}
                                        className="mt-8 group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-gold px-6 text-sm font-bold text-zinc-950 glow-gold hover:bg-gold-400 transition-all hover:-translate-y-0.5"
                                    >
                                        Explore for {a.label}
                                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                    </button>
                                </div>
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>
            </div>
        </section>
    );
}
